/** @module @category Store */ import { useCallback, useMemo, useSyncExternalStore } from "react";
function bindActions(store, actions) {
    if (typeof actions == 'function') {
        actions = actions(store);
    }
    const bound = {};
    for (const [actionName, actionFunction] of Object.entries(actions)){
        const boundFunction = function(...args) {
            store.setState((state)=>{
                return actionFunction(state, ...args);
            });
        };
        bound[actionName] = boundFunction;
    }
    return bound;
}
const defaultSelectFunction = ()=>(x)=>x;
function useStore(store, select = defaultSelectFunction(), actions) {
    // Use useSyncExternalStore to subscribe synchronously during render
    // This ensures React can properly track all state updates
    const subscribe = useCallback((callback)=>{
        return store.subscribe(callback);
    }, [
        store
    ]);
    const getSnapshot = useCallback(()=>select(store.getState()), [
        store,
        select
    ]);
    const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    let boundActions = useMemo(()=>actions ? bindActions(store, actions) : {}, [
        store,
        actions
    ]);
    return {
        ...state,
        ...boundActions
    };
}
/**
 *
 * @param store A zustand store
 * @param actions
 * @returns
 */ function useStoreWithActions(store, actions) {
    return useStore(store, defaultSelectFunction(), actions);
}
/**
 * Whenever possible, use `useStore(yourStore)` instead. This function is for creating a
 * custom hook for a specific store.
 */ function createUseStore(store) {
    function useStore(actions) {
        const subscribe = useCallback((callback)=>store.subscribe(callback), []);
        const getSnapshot = useCallback(()=>store.getState(), []);
        const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
        let boundActions = useMemo(()=>actions ? bindActions(store, actions) : {}, [
            actions
        ]);
        return {
            ...state,
            ...boundActions
        };
    }
    return useStore;
}
export { createUseStore, useStore, useStoreWithActions };
